import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { scheduledCallService } from "@/services/scheduledCallService";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
}

interface ScheduleLeadCallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead;
  onScheduled: () => void;
  mode: "schedule" | "reschedule";
  existingScheduledTime?: string | null;
  existingCallRequestId?: string | null;
  campaignId?: string | null; // Add campaignId prop
}

export function ScheduleLeadCallDialog({
  isOpen,
  onClose,
  lead,
  onScheduled,
  mode,
  existingScheduledTime,
  existingCallRequestId,
  campaignId, // Add campaignId to props
}: ScheduleLeadCallDialogProps) {
  const { toast } = useToast();
  // Get current time + 2 minutes for default
  const getDefaultScheduleTime = () => {
    const defaultTime = new Date();
    defaultTime.setMinutes(defaultTime.getMinutes() + 2);
    return defaultTime;
  };

  const defaultTime = getDefaultScheduleTime();

  const [date, setDate] = useState<Date | undefined>(
    existingScheduledTime ? new Date(existingScheduledTime) : defaultTime,
  );
  const [hour, setHour] = useState<string>(
    existingScheduledTime
      ? format(new Date(existingScheduledTime), "HH")
      : format(defaultTime, "HH"),
  );
  const [minute, setMinute] = useState<string>(
    existingScheduledTime
      ? format(new Date(existingScheduledTime), "mm")
      : format(defaultTime, "mm"),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null,
  );
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<
    string | null
  >(null);
  const [assistants, setAssistants] = useState<
    Array<{ id: string; name: string; variables?: string[] }>
  >([]);
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {}
  );
  const [phoneNumbers, setPhoneNumbers] = useState<
    Array<{ id: string; phone_number: string }>
  >([]);
  const [loadingAssistants, setLoadingAssistants] = useState(true);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(true);
  const { currentOrganization } = useOrganization();

  const getSelectedAssistantVariables = () => {
    if (!selectedAssistantId) return [];
    const assistant = assistants.find((a) => a.id === selectedAssistantId);
    return assistant?.variables || [];
  };

  // Fetch assistants and phone numbers when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchAssistants();
      fetchPhoneNumbers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedAssistantId) {
      const variables = getSelectedAssistantVariables();
      if (variables.length === 0) {
        setVariableValues({});
      } else {
        const initialValues: Record<string, string> = {};
        variables.forEach((variable) => {
          if (variable) {
            initialValues[variable] = "";
          }
        });
        setVariableValues(initialValues);
      }
    } else {
      setVariableValues({});
    }
  }, [selectedAssistantId, assistants]);

  const fetchAssistants = async () => {
    try {
      setLoadingAssistants(true);
      const { data, error } = await supabase
        .from("assistants")
        .select("id, name, variables")
        .eq("status", "published")
        .order("name");

      if (error) throw error;
      setAssistants(data || []);

      // Auto-select the first assistant if available
      if (data && data.length > 0 && !selectedAssistantId) {
        setSelectedAssistantId(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching assistants:", error);
      toast({
        title: "Error",
        description: "Failed to load assistants. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingAssistants(false);
    }
  };

  const fetchPhoneNumbers = async () => {
    try {
      setLoadingPhoneNumbers(true);
      const { data, error } = await supabase
        .from("phone_numbers")
        .select("id, phone_number")
        .eq("type", "outbound")
        .order("phone_number");

      if (error) throw error;
      setPhoneNumbers(data || []);

      // Auto-select the first phone number if available
      if (data && data.length > 0 && !selectedPhoneNumberId) {
        setSelectedPhoneNumberId(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
      toast({
        title: "Error",
        description: "Failed to load phone numbers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const handleSchedule = async () => {
    if (!date) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAssistantId) {
      toast({
        title: "Error",
        description: "Please select an assistant",
        variant: "destructive",
      });
      return;
    }

    if (!selectedPhoneNumberId) {
      toast({
        title: "Error",
        description: "Please select a phone number",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create a new date with the selected date and time in the local timezone
      const scheduledDate = new Date(date);
      scheduledDate.setHours(parseInt(hour, 10));
      scheduledDate.setMinutes(parseInt(minute, 10));
      scheduledDate.setSeconds(0);
      scheduledDate.setMilliseconds(0);

      // Check if the date is in the past
      if (scheduledDate < new Date()) {
        toast({
          title: "Error",
          description: "Cannot schedule a call in the past",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Log timezone information for debugging
      console.log("Local time selected:", scheduledDate.toString());
      console.log("UTC time (ISO format):", scheduledDate.toISOString());
      console.log(
        "Local timezone offset (minutes):",
        scheduledDate.getTimezoneOffset(),
      );

      // Create or reschedule a call using the scheduledCallService
      if (mode === "schedule") {
        // Create a new scheduled call
        await scheduledCallService.createScheduledCall(
          {
            lead_id: lead.id,
            assistant_id: selectedAssistantId,
            phone_number_id: selectedPhoneNumberId,
            campaign_id: campaignId, // Include campaignId in the form data
            scheduled_time: scheduledDate.toISOString(),
            custom_variables: {
              lead_first_name: lead.first_name,
              lead_last_name: lead.last_name,
              lead_phone_number: lead.phone_number,
              campaign_id: campaignId, // Include campaignId in variables too
            },
            assistant_variables: variableValues,
          }
        );
      } else {
        // Reschedule an existing call
        if (!existingCallRequestId) {
          throw new Error("Scheduled call ID is required for rescheduling");
        }

        await scheduledCallService.rescheduleCall(
          existingCallRequestId,
          scheduledDate.toISOString(),
        );
      }

      toast({
        title: "Success",
        description: `Call ${
          mode === "schedule" ? "scheduled" : "rescheduled"
        } successfully`,
      });

      onScheduled();
      onClose();
    } catch (error) {
      console.error(`Error ${mode}ing call:`, error);
      toast({
        title: "Error",
        description: `Failed to ${mode} call. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Generate hours for the select in AM/PM format
  const hours = Array.from({ length: 12 }, (_, i) => {
    const hour = i === 0 ? 12 : i;
    return { value: i.toString().padStart(2, "0"), label: `${hour} AM` };
  }).concat(
    Array.from({ length: 12 }, (_, i) => {
      const hour = i === 0 ? 12 : i;
      const value = (i + 12).toString().padStart(2, "0");
      return { value, label: `${hour} PM` };
    }),
  );

  // Generate minutes for the select (all minutes, not just 5-minute increments)
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === "schedule" ? "Schedule Call" : "Reschedule Call"}
          </DialogTitle>
          <DialogDescription>
            Schedule a call with {lead.first_name} {lead.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Assistant</label>
            <Select
              value={selectedAssistantId || ""}
              onValueChange={setSelectedAssistantId}
              disabled={loadingAssistants}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an assistant" />
              </SelectTrigger>
              <SelectContent>
                {assistants.map((assistant) => (
                  <SelectItem key={assistant.id} value={assistant.id}>
                    {assistant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {assistants.length === 0 && !loadingAssistants && (
              <p className="text-xs text-red-500">
                No assistants available. Please create an assistant first.
              </p>
            )}
          </div>

          {selectedAssistantId &&
            getSelectedAssistantVariables().length > 0 && (
              <div className="mt-4 border border-border rounded-md p-3">
                <Label className="mb-2 block">Template Variables</Label>
                <div className="grid grid-cols-3 gap-4">
                  {getSelectedAssistantVariables().map((variable) => (
                    <div key={variable} className="space-y-1">
                      <Label
                        htmlFor={`var-${variable}`}
                        className="text-xs text-muted-foreground"
                      >
                        {variable}
                      </Label>
                      <Input
                        id={`var-${variable}`}
                        value={variableValues[variable] || ""}
                        onChange={(e) =>
                          setVariableValues((prev) => ({
                            ...prev,
                            [variable]: e.target.value,
                          }))
                        }
                        placeholder={`Value for ${variable}`}
                        className="h-8 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  These values will replace {"{{variable}}"} placeholders
                  in the assistant's prompts
                </p>
              </div>
            )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Outbound Phone Number</label>
            <Select
              value={selectedPhoneNumberId || ""}
              onValueChange={setSelectedPhoneNumberId}
              disabled={loadingPhoneNumbers}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {phoneNumbers.map((phoneNumber) => (
                  <SelectItem key={phoneNumber.id} value={phoneNumber.id}>
                    {phoneNumber.phone_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {phoneNumbers.length === 0 && !loadingPhoneNumbers && (
              <p className="text-xs text-red-500">
                No outbound phone numbers available. Please add a phone number
                first.
              </p>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium">Date</label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              className="rounded-md border"
              disabled={(date) =>
                date < new Date(new Date().setHours(0, 0, 0, 0))
              }
            />
          </div>

          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Hour</label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger>
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent>
                  {hours.map((h) => (
                    <SelectItem key={h.value} value={h.value}>
                      {h.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium">Minute</label>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger>
                  <SelectValue placeholder="Minute" />
                </SelectTrigger>
                <SelectContent>
                  {minutes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSchedule} disabled={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                {mode === "schedule" ? "Scheduling..." : "Rescheduling..."}
              </>
            ) : (
              <>{mode === "schedule" ? "Schedule" : "Reschedule"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
